import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import {
  filterBy,
  CompositeFilterDescriptor,
} from "@progress/kendo-data-query";
import {
  Grid as KendoGrid,
  GridColumn as Column,
  GridCellProps,
  GridFilterChangeEvent,
  GridPageChangeEvent,
  GridCustomHeaderCellProps,
  GridCustomCellProps,
  GridRowProps,
} from "@progress/kendo-react-grid";
import { GridLayout, GridLayoutItem } from "@progress/kendo-react-layout";
import { HeaderThElement } from "@progress/kendo-react-data-tools";
import { Typography } from "@progress/kendo-react-common";
import { Button } from "@progress/kendo-react-buttons";
import { FiEdit } from "react-icons/fi";
import { LoadingPanel } from "../../components/layout/Loading";
import IconButton from "../../components/common/IconButton";
import AppDialog, { DialogCloseButton } from "../../components/dialog/Dialog";
import { MdDelete } from "react-icons/md";
import { closeDialog, openDialog } from "../../components/dialog/dialogSlice";
import { setCurrentPage, setPageLimit } from "../pagination/paginationSlice";
import { checkAcessRights, formatIndianNumber } from "../../_helper/helper";
import {
  DELETE_ACCESS,
  EDIT_ACCESS,
  FINANCIAL_YEAR,
  INSERT_ACCESS,
  PRINT_ACCESS,
} from "../../_contstants/common";
import {
  deleteSalesOrder,
  getAllSalesOrders,
  getSalesOrderChildList,
} from "./services/salesOrder.services";
import {
  clearFilterSalesOrder,
  clearSalesOrderChildList,
  clearSalesOrderID,
  setFilterSalesOrder,
  setSalesOrderID,
} from "./salesOrderSlice";
import { IoPrint } from "react-icons/io5";
import { clearItemList } from "../Item/itemSlice";
import { FaEye } from "react-icons/fa6";
import { Tooltip } from "@progress/kendo-react-tooltip";
import AlertBox from "../../components/common/AlertBox";
import _debounce from "lodash/debounce";
import { filterClearIcon } from "@progress/kendo-svg-icons";
import { TextBox } from "@progress/kendo-react-inputs";

const SalesOrder: React.FC = () => {
  const navigate = useNavigate();
  const isAddAccess = checkAcessRights(location.pathname, INSERT_ACCESS);

  const handleCreate = () => {
    navigate("/order");
  };

  return (
    <>
      <DeleteSalesOrderDialog />
      <ChildSalesOrdersDialog />
      <GridLayout cols={[{ width: "100%" }]}>
        <GridLayoutItem
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            padding: "4px 20px 20px 0",
          }}
        >
          <Typography.h4 style={{ marginBottom: 0, marginLeft: "3px" }}>
            Sales Order List
          </Typography.h4>
          {isAddAccess && (
            <Button
              onClick={handleCreate}
              fillMode={"solid"}
              themeColor={"primary"}
            >
              {"Add New"}
            </Button>
          )}
        </GridLayoutItem>
        <GridLayoutItem>
          <SalesOrderGridComponent />
        </GridLayoutItem>
      </GridLayout>
    </>
  );
};

const SalesOrderGridComponent: React.FC = () => {
  const gridRef = useRef<any>(null);
  const dispatch = useAppDispatch();
  const isEditBtnRef = useRef<any>(false);
  const navigate = useNavigate();
  const isEditAccess = checkAcessRights(location.pathname, EDIT_ACCESS);
  const isDeleteAccess = checkAcessRights(location.pathname, DELETE_ACCESS);
  const isPrintAccess = checkAcessRights(location.pathname, PRINT_ACCESS);

  const SalesOrderList = useAppSelector(
    (state) => state.salesOrder.SalesOrderList
  );
  const loading = useAppSelector((state) => state.salesOrder.loading);
  const currentPage = useAppSelector((state) => state.pagination.currentPage);
  const pageLimit = useAppSelector((state) => state.pagination.pageLimit);
  const FilterSalesOrder = useAppSelector(
    (state) => state.salesOrder.FilterSalesOrder
  );

  useEffect(() => {
    return () => {
      dispatch(clearItemList());
      dispatch(closeDialog());
      dispatch(setCurrentPage(0));
      if (!isEditBtnRef.current) {
        dispatch(clearFilterSalesOrder());
      }
    };
  }, []);

  useEffect(() => {
    const payload = {
      ...FilterSalesOrder,
      financial_year: FINANCIAL_YEAR,
      limit: +pageLimit,
      skip: +currentPage * +pageLimit,
    };
    dispatch(getAllSalesOrders(payload));
  }, [currentPage, pageLimit]);

  const [filter, setFilter] = React.useState<
    CompositeFilterDescriptor | undefined
  >(undefined);

  const pageChange = (event: GridPageChangeEvent) => {
    const newPage = event.page.skip / event.page.take;
    dispatch(setPageLimit(event.page.take));
    dispatch(setCurrentPage(newPage));
  };

  const handleChildView = async (props: any) => {
    const res = await dispatch(getSalesOrderChildList(props.dataItem?.id));
    if (res.meta.requestStatus === "fulfilled") {
      dispatch(openDialog("childSalesOrders"));
    }
  };

  const MyEditCommandCell = (props: GridCellProps) => {
    const handleEdit = (sale_order_guid: any) => {
      navigate("/salesorder/edit", {
        state: { sale_order_guid: sale_order_guid },
      });
    };

    const handleOpenDeleteDialog = (sale_order_guid: any) => {
      dispatch(openDialog("deleteSalesOrder"));
      dispatch(setSalesOrderID(sale_order_guid));
    };
    const handlePrint = (sale_order_guid: any) => {
      window.open(`/salesorderprint/${sale_order_guid}`);
    };

    return (
      <td
        className="action-td"
        style={{
          ...props.style,
        }}
      >
        {isEditAccess && (
          <IconButton
            type="button"
            fillMode="flat"
            size="small"
            title="Edit"
            ref={isEditBtnRef}
            onClick={() => {
              isEditBtnRef.current = true;
              handleEdit(props.dataItem?.sale_order_guid);
            }}
          >
            <FiEdit
              className="absolute-position"
              style={{ fontSize: "20px" }}
            />
          </IconButton>
        )}
        {isDeleteAccess && (
          <IconButton
            type="button"
            fillMode="flat"
            size="small"
            title="Delete"
            disabled={props.dataItem?.ischild}
            onClick={() =>
              handleOpenDeleteDialog(props.dataItem?.sale_order_guid)
            }
          >
            <MdDelete
              className="absolute-position"
              style={{ fontSize: "24px" }}
            />
          </IconButton>
        )}
        {isPrintAccess && (
          <IconButton
            type="button"
            fillMode="flat"
            size="small"
            title="Print"
            onClick={() => handlePrint(props.dataItem?.sale_order_guid)}
          >
            <IoPrint
              className="absolute-position"
              style={{ fontSize: "20px" }}
            />
          </IconButton>
        )}
      </td>
    );
  };

  const rowRender = (
    trElement: React.ReactElement<HTMLTableRowElement>,
    props: GridRowProps
  ) => {
    const isdelivered = props.dataItem.isdelivered;
    const deliver = { backgroundColor: "#ffdada" };
    const notDelivered = { backgroundColor: "transparent" };
    const trProps: any = { style: isdelivered ? notDelivered : deliver };
    let childrenArray: React.ReactNode[] = [];

    if (trElement.props.children) {
      if (trElement.props.children instanceof HTMLCollection) {
        childrenArray = Array.from(trElement.props.children).map(
          (child: any) => {
            return React.isValidElement(child) ? child : <>{child}</>;
          }
        );
      } else {
        childrenArray = [trElement.props.children];
      }
    }

    return React.cloneElement(trElement, { ...trProps }, childrenArray);
  };

  const handleFilterChange = (e: GridFilterChangeEvent) => {
    setFilter(e.filter);
  };
  const HeaderCustomCell = (props: GridCustomHeaderCellProps) => (
    <HeaderThElement
      columnId={props.thProps?.columnId || ""}
      {...props.thProps}
      className="table-header"
    >
      {props.children}
    </HeaderThElement>
  );

  const CustomCell = (props: any) => {
    return (
      <td {...props.tdProps} colSpan={1} className={"table-cell"}>
        {props.children}
      </td>
    );
  };

  const MyDataCustomCell = (props: GridCustomCellProps) => (
    <CustomCell {...props} />
  );

  return (
    <>
      {loading && <LoadingPanel gridRef={gridRef} />}
      <KendoGrid
        rowRender={rowRender}
        style={{ maxHeight: "calc(100vh - 152px)" }}
        className="responsive-table"
        filterable={true}
        filter={filter}
        data={filter ? filterBy(SalesOrderList, filter) : SalesOrderList}
        onFilterChange={handleFilterChange}
        total={SalesOrderList[0]?.itemcount || SalesOrderList?.length}
        skip={currentPage * pageLimit}
        take={pageLimit}
        pageable={{
          buttonCount: 5,
          pageSizes: [10, 50, 100, 300, 500],
          pageSizeValue: pageLimit,
          type: "input",
        }}
        onPageChange={pageChange}
        cells={{
          headerCell: HeaderCustomCell,
          data: MyDataCustomCell,
        }}
      >
        <Column
          field="sales_order_no"
          title="Sales Order No"
          cell={(props: any) => (
            <td className="d-flex align-items-center gap-1">
              <div
                className={isEditAccess ? "hoverableName" : "normalName"}
                onClick={() =>
                  isEditAccess &&
                  navigate("/salesorder/edit", {
                    state: {
                      sale_order_guid: props.dataItem?.sale_order_guid,
                    },
                  })
                }
              >
                {props.dataItem?.sales_order_no}
              </div>
              {props.dataItem?.ischild && (
                <div
                  style={{ cursor: "pointer" }}
                  onClick={() => handleChildView(props)}
                >
                  <Tooltip
                    anchorElement="target"
                    position="top"
                    parentTitle={true}
                  >
                    <FaEye
                      title="View"
                      style={{ fontSize: 17, color: "mediumseagreen" }}
                    />
                  </Tooltip>
                </div>
              )}
            </td>
          )}
        />
        <Column field="sales_date" title="Sales Order Date" />
        <Column
          field="dealer_name"
          title="Dealer"
          filterCell={CommonFilterCell}
          cell={(props: any) => <td>{props.dataItem?.account_name}</td>}
        />
        <Column
          field="gst"
          title="GST"
          cell={(props: any) => (
            <td style={{ textAlign: "end" }}>
              {formatIndianNumber(+props.dataItem?.gst)}
            </td>
          )}
        />
        <Column
          field="total_amount"
          title="Total Amount"
          cell={(props: any) => (
            <td className="text-end">
              {props.dataItem.total_amount
                ? formatIndianNumber(props.dataItem.total_amount)
                : null}
            </td>
          )}
        />
        {(isEditAccess || isDeleteAccess || isPrintAccess) && (
          <Column
            field="id"
            title="Actions"
            cell={MyEditCommandCell}
            width={"140px"}
            filterable={false}
          />
        )}
      </KendoGrid>
    </>
  );
};

const DeleteSalesOrderDialog: React.FC = () => {
  const dispatch = useAppDispatch();
  const SalesOrderID = useAppSelector((state) => state.salesOrder.SalesOrderID);
  const dialogName = useAppSelector((state) => state.dialog.dialogName);
  const currentPage = useAppSelector((state) => state.pagination.currentPage);
  const pageLimit = useAppSelector((state) => state.pagination.pageLimit);
  const FilterSalesOrder = useAppSelector(
    (state) => state.salesOrder.FilterSalesOrder
  );

  const handleDeleteAction = async (ID: any) => {
    if (ID) {
      const response = await dispatch(deleteSalesOrder(ID));
      dispatch(setCurrentPage(0));
      const payload = {
        ...FilterSalesOrder,
        ...FilterSalesOrder,
        financial_year: FINANCIAL_YEAR,
        limit: +pageLimit,
        skip: +currentPage * +pageLimit,
      };
      if (response.payload.Data.Status === 200) {
        dispatch(getAllSalesOrders(payload));
        dispatch(closeDialog());
        dispatch(clearSalesOrderID());
      } else {
        dispatch(getAllSalesOrders(payload));
        dispatch(closeDialog());
        dispatch(clearSalesOrderID());
      }
    }
  };

  return (
    <>
      {dialogName === "deleteSalesOrder" && (
        <AppDialog>
          <>
            <Typography.h5>{"Delete Sales Order"}</Typography.h5>
            <GridLayout>
              <GridLayoutItem>
                <Typography.p>
                  Are you sure you want to delete this Sales Order?
                </Typography.p>
              </GridLayoutItem>
              <GridLayoutItem
                style={{
                  display: "flex",
                  justifyContent: "end",
                }}
              >
                <DialogCloseButton themeColor="error" />
                <Button
                  fillMode={"solid"}
                  themeColor={"error"}
                  onClick={() => handleDeleteAction(SalesOrderID)}
                  type="button"
                >
                  Delete
                </Button>
              </GridLayoutItem>
            </GridLayout>
          </>
        </AppDialog>
      )}
    </>
  );
};

const ChildSalesOrdersDialog: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  // const gridRef = useRef<any>(null);
  const dialogName = useAppSelector((state) => state.dialog.dialogName);
  const SalesOrderChildList = useAppSelector(
    (state) => state.salesOrder.SalesOrderChildList
  );
  const isEditAccess = checkAcessRights(location.pathname, EDIT_ACCESS);
  const isDeleteAccess = checkAcessRights(location.pathname, DELETE_ACCESS);
  const isPrintAccess = checkAcessRights(location.pathname, PRINT_ACCESS);

  useEffect(() => {
    return () => {
      dispatch(clearSalesOrderChildList());
    };
  }, []);

  const MyEditCommandCell = (item: any) => {
    const handleEdit = (sale_order_guid: any) => {
      navigate("/salesorder/edit", {
        state: { sale_order_guid: sale_order_guid },
      });
    };

    const handleOpenDeleteDialog = (sale_order_guid: any) => {
      dispatch(openDialog("deleteSalesOrder"));
      dispatch(setSalesOrderID(sale_order_guid));
    };

    const handlePrint = (sale_order_guid: any) => {
      window.open(`/salesorderprint/${sale_order_guid}`);
    };

    return (
      <td className="action-td">
        {isEditAccess && (
          <IconButton
            type="button"
            fillMode="flat"
            size="small"
            title="Edit"
            onClick={() => handleEdit(item?.item?.sale_order_guid)}
          >
            <FiEdit
              className="absolute-position"
              style={{ fontSize: "20px" }}
            />
          </IconButton>
        )}
        {isDeleteAccess && (
          <IconButton
            type="button"
            fillMode="flat"
            size="small"
            title="Delete"
            onClick={() => handleOpenDeleteDialog(item?.item?.sale_order_guid)}
          >
            <MdDelete
              className="absolute-position"
              style={{ fontSize: "24px" }}
            />
          </IconButton>
        )}
        {isPrintAccess && (
          <IconButton
            type="button"
            fillMode="flat"
            size="small"
            title="Print"
            onClick={() => handlePrint(item?.item?.sale_order_guid)}
          >
            <IoPrint
              className="absolute-position"
              style={{ fontSize: "20px" }}
            />
          </IconButton>
        )}
      </td>
    );
  };

  // if (loading) return <LoadingPanel gridRef={gridRef} />;
  return (
    <>
      {dialogName === "childSalesOrders" && (
        <AppDialog>
          <>
            <Typography.h5>{"Sales Orders"}</Typography.h5>
            <GridLayout>
              <GridLayoutItem>
                {SalesOrderChildList && SalesOrderChildList?.length > 0 ? (
                  <div className="table-responsive">
                    <table className="table table-bordered">
                      <thead>
                        <tr>
                          <th style={{ background: "black", color: "white" }}>
                            Sales Order No
                          </th>
                          <th style={{ background: "black", color: "white" }}>
                            Sales Order Date
                          </th>
                          <th style={{ background: "black", color: "white" }}>
                            Dealer
                          </th>
                          <th style={{ background: "black", color: "white" }}>
                            GST
                          </th>
                          <th style={{ background: "black", color: "white" }}>
                            Total Amount
                          </th>
                          <th style={{ background: "black", color: "white" }}>
                            Actions
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {SalesOrderChildList?.map(
                          (item: any, index: number) => (
                            <tr key={index}>
                              <td>{item?.modify_order_no}</td>
                              <td>{item?.sales_date}</td>
                              <td className="text-end">{item?.account_name}</td>
                              <td className="text-end">
                                {item?.gst ? formatIndianNumber(item?.gst) : ""}
                              </td>
                              <td className="text-end">
                                {item?.total_amount
                                  ? formatIndianNumber(item?.total_amount)
                                  : ""}
                              </td>
                              <td>
                                <MyEditCommandCell item={item} />
                              </td>
                            </tr>
                          )
                        )}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <AlertBox style={{ marginBottom: 10 }} />
                )}
              </GridLayoutItem>
              <GridLayoutItem
                style={{
                  display: "flex",
                  justifyContent: "end",
                }}
              >
                <DialogCloseButton themeColor="error" />
              </GridLayoutItem>
            </GridLayout>
          </>
        </AppDialog>
      )}
    </>
  );
};

const CommonFilterCell = (props: any) => {
  const dispatch = useAppDispatch();
  const currentPage = useAppSelector((state) => state.pagination.currentPage);
  const pageLimit = useAppSelector((state) => state.pagination.pageLimit);
  const FilterSalesOrder: any = useAppSelector(
    (state) => state.salesOrder.FilterSalesOrder
  );
  const isFilterRef = useRef(true);
  const [inputValue, setInputValue] = useState(props.value || "");
  const [inputField, setInputField] = useState(props.field || "");

  const onChange = (event: any) => {
    setInputValue(event.target.value);
    setInputField(event.target.name);
  };

  const delayedApiCall = React.useCallback(
    _debounce((updatedFilters: any) => {
      dispatch(setCurrentPage(0));
      const payload = {
        ...updatedFilters,
        financial_year: FINANCIAL_YEAR,
        limit: +pageLimit,
        skip: 0 * +pageLimit,
      };

      dispatch(getAllSalesOrders(payload));
    }, 300),
    [dispatch]
  );

  useEffect(() => {
    if (!isFilterRef.current) {
      const updatedFilters = {
        ...FilterSalesOrder,
        [inputField]: inputValue,
      };
      dispatch(setFilterSalesOrder(updatedFilters));
      delayedApiCall(updatedFilters);
    } else {
      isFilterRef.current = false;
    }
    return () => {
      delayedApiCall.cancel();
    };
  }, [inputValue, delayedApiCall]);

  const onClearButtonClick = (event: any) => {
    event.preventDefault();
    props.onChange({
      value: null,
      operator: "",
      syntheticEvent: event,
    });

    const updatedFilters = { ...FilterSalesOrder, [props.field]: "" };
    dispatch(setFilterSalesOrder(updatedFilters));

    const payload = {
      ...updatedFilters,
      financial_year: FINANCIAL_YEAR,
      limit: +pageLimit,
      skip: +currentPage * +pageLimit,
    };

    dispatch(getAllSalesOrders(payload));
  };

  return (
    <div className="d-flex">
      <TextBox
        value={FilterSalesOrder[props.field] || ""}
        name={props.field || ""}
        onChange={onChange}
      />
      <Button
        svgIcon={filterClearIcon}
        title="Clear"
        disabled={!FilterSalesOrder[props.field]}
        onClick={onClearButtonClick}
        type="button"
        style={{
          marginLeft: "8px",
        }}
      ></Button>
    </div>
  );
};

export default SalesOrder;
